import { Component, EventEmitter, HostBinding, Input, OnInit, OnDestroy, Output, ViewChild } from '@angular/core';
import { HttpErrorService, SvcMediaQuery } from 'projects/lib-shared-common/src/public-api';
import { catchError, debounceTime, finalize, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { SvcFeedService } from '../../services/svc-feed.service';
import { FormControl } from '@angular/forms';
import { ISvcChipOption } from '@solvace/component/svc-controls';
import { SvcFilterEvent, SvcFilterField, SvcFiltersComponent } from '@solvace/component/svc-filters';
import { SvcToastService } from '@solvace/component/svc-toast';
import { FeedItem, SvcFeedNoticationType } from '../../models/feed-item.model';
import { TranslocoService } from '@ngneat/transloco';
import { Application } from '../../models/applications.model';
import { SvcCommentsViewerComponent } from '../svc-comments-viewer/svc-comments-viewer.component';
import { SvcCommentsListComponent } from '../svc-comments-list/svc-comments-list.component';
import { SvcComment } from '../../models/comment.model';

@Component({
  selector: 'svc-comments-feed',
  templateUrl: './svc-comments-feed.component.html',
  styleUrls: ['./svc-comments-feed.component.scss'],
})
export class SvcCommentsFeedComponent implements OnInit, OnDestroy {
  @HostBinding('class.expanded') expanded: boolean = false;

  @ViewChild('svcFilters') public svcFilters!: SvcFiltersComponent;
  @ViewChild('svcCommentsViewer') public svcCommentsViewer!: SvcCommentsViewerComponent;
  @ViewChild('svcCommentsList') public svcCommentsList!: SvcCommentsListComponent;

  @Input() externalFeature: boolean = false;

  @Output() public onTotalChanged = new EventEmitter<number>();
  @Output() public onLoadingChanged = new EventEmitter<boolean>();
  @Output() public onExpanded = new EventEmitter<boolean>();
  @Output() public onHasErrorFeed = new EventEmitter<boolean>();
  @Output() public onHasErrorTotalUnread = new EventEmitter<boolean>();
  @Output() public onCloseSidebarWasNedded = new EventEmitter<boolean>();

  public feedAreOver = false;
  public searchCtrl = new FormControl('');
  public feeds: FeedItem[] = [];
  public total: number = 0;
  public selectedFeed: FeedItem;
  public isLoading: boolean = true;
  public isTotalLoading: boolean = true;
  public isFiltersLoading: boolean = true;
  public isCheckingFeedCanBeLoaded: boolean = false;
  public showFilters: boolean = false;
  public lastFilters: {
    filter?: string;
    applicationIds?: string[];
    startDate?: string;
    endDate?: string;
  } = {
      filter: '',
      applicationIds: [],
      startDate: null,
      endDate: null,
    };
  public hasFiltersDefined = false;
  public hasFiltersPending = false;
  public filtersField: SvcFilterField[] = [];

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private _svcMediaQuery: SvcMediaQuery,
    private _svcFeedService: SvcFeedService,
    private _translocoService: TranslocoService,
    private _svcToastService: SvcToastService,
    private _httpErrorService: HttpErrorService
  ) {}

  ngOnInit(): void {
    this._listenMediaQuery();
    this._listenFeedList();
    this._listenFeedErrors();
    this._listenTotaUnread();
    this._listenSearchControl();
    this._listenSignalR();
    this._listenFeedRemoved();
    this._getFilters();
    this._svcFeedService.getFeed();
    this._svcFeedService.getTotalUnread();

    this._svcFeedService.hasErrorFeed$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      Promise.resolve().then(() => this.onHasErrorFeed.emit(value));
    });

    this._svcFeedService.hasErrorTotalUnread$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      Promise.resolve().then(() => this.onHasErrorTotalUnread.emit(value));
    });
  }

  public toggleExpanded(options?: { dontClearSelectedFeed: boolean }) {
    this.expanded = !this.expanded;
    if (this.expanded && this.feeds.length > 0 && !this.selectedFeed) {
      const firstFeed = this.feeds[0];
      this.onFeedClick(firstFeed);
    } else if (!this.expanded && this.selectedFeed && !options?.dontClearSelectedFeed) {
      this.selectedFeed = null;
    }
    this.onExpanded.emit(this.expanded);
  }

  public onFeedClick(feed: FeedItem) {
    this.isCheckingFeedCanBeLoaded = true;
    this._svcFeedService
      .getUserHasPermission({
        commentHeaderId: feed.commentHeaderId,
        registryId: feed.registryId || feed.registryUid,
      })
      .pipe(
        switchMap((hasPermission) => {
          if (hasPermission) {
            this.selectedFeed = feed;
            if (feed.totalNoRead > 0 || feed.isMarkedUnRead) {
              return this._svcFeedService.markAsReadByCommentHeaderId(feed.commentHeaderId).pipe(
                tap(() => {
                  feed.totalNoRead = 0;
                  feed.isMarkedUnRead = false;
                  this.subtractMinusOneInTotal();
                }),
                catchError(() => of(true))
              );
            }
          } else {
            const message = this._translocoService.translate('Você não possui permissão acessar as mensagens deste feed.');
            this._svcToastService.error(message);
          }
          return of(true);
        }),
        catchError((error) => {
          const isRecordDeleted = error?.status === 404 || error?.status === 410;
          if (isRecordDeleted) {
            const message = this._translocoService.translate(
              'Este chat não está mais disponível, pois o registro foi removido.'
            );
            this._svcToastService.error(message);
          } else {
            this._httpErrorService.showErrorInToast(error);
          }
          return of(null);
        }),
        finalize(() => (this.isCheckingFeedCanBeLoaded = false))
      )
      .subscribe();
  }

  public onCommentAdded(comment: SvcComment): void {
    this.selectedFeed = this._svcFeedService.updateCommentedFeedToAtStartOfFeeds(
      comment,
      this.selectedFeed?.commentId
    );
  }

  public addOneMoreInTotal() {
    this._tryToUpdateTotal(this.total + 1);
  }

  public subtractMinusOneInTotal() {
    this._tryToUpdateTotal(this.total - 1);
  }

  private _tryToUpdateTotal(total: number) {
    if (!this.isTotalLoading) {
      this.total = Math.max(total, 0);
      this.onTotalChanged.emit(this.total);
    }
  }

  public openSelectedFeedModule() {
    this.svcCommentsList?.openFeedModule(this.selectedFeed);
  }

  public nextPage() {
    if (!this.feedAreOver && !this.isLoading) {
      this._svcFeedService.feedNextPage();
    }
  }

  public clearFilters() {
    if (!this.isFiltersLoading && !this.svcFilters?.isEmpty) {
      this.svcFilters.clearFiltersField({ preventEmit: true });
      this.hasFiltersDefined = false;
      this.hasFiltersPending = false;
      this.doFilter({ fields: this.svcFilters.fields });
    }
  }

  public checkIfHasFiltersToDo(event: SvcFilterEvent) {
    const newFilters = {
      filter: this.searchCtrl.value ?? '',
      applicationIds: event.fields.find((f) => f.name === 'applicationIds')?.value ?? null,
      startDate: event.fields.find((f) => f.name === 'startDate')?.value ?? null,
      endDate: event.fields.find((f) => f.name === 'endDate')?.value ?? null,
    };
    this.hasFiltersPending = JSON.stringify(newFilters) != JSON.stringify(this.lastFilters);
  }

  public doFilter(event: SvcFilterEvent) {
    const newFilters = {
      filter: this.searchCtrl.value ?? '',
      applicationIds: event.fields.find((f) => f.name === 'applicationIds')?.value ?? null,
      startDate: event.fields.find((f) => f.name === 'startDate')?.value ?? null,
      endDate: event.fields.find((f) => f.name === 'endDate')?.value ?? null,
    };

    if (JSON.stringify(this.lastFilters ?? {}) !== JSON.stringify(newFilters)) {
      this.lastFilters = newFilters;
      this.hasFiltersPending = false;
      this._checkIfHasFilters();
      this._svcFeedService.getFeed(this.lastFilters);
    }
    this.closeFilters({ fromSuccess: true });
  }

  public refresh() {
    this._svcFeedService.getFeed(this.lastFilters);
  }

  public openFilters() {
    this.showFilters = true;
  }

  public closeFilters(options?: { fromSuccess: boolean }) {
    this.showFilters = false;
    if (this.hasFiltersPending && !options?.fromSuccess) {
      this.svcFilters.patchValues(this.lastFilters);
      this.hasFiltersPending = false;
    }
  }

  private _listenMediaQuery() {
    this._svcMediaQuery.size$
      .pipe(
        takeUntil(this.destroy$),
        tap((size) => {
          if (size.isBelowMD) {
            Promise.resolve().then(() => (this.expanded = false));
          }
        })
      )
      .subscribe();
  }

  private _listenFeedErrors() {
    this._svcFeedService.hasErrorFeed$
      .pipe(
        takeUntil(this.destroy$),
        tap((value) => {
          this.onHasErrorFeed.emit(value);
        })
      )
      .subscribe();

    this._svcFeedService.hasErrorTotalUnread$
      .pipe(
        takeUntil(this.destroy$),
        tap((value) => {
          this.onHasErrorTotalUnread.emit(value);
        })
      )
      .subscribe();
  }

  private _listenFeedList() {
    this._svcFeedService.feedIsLoading$
      .pipe(
        takeUntil(this.destroy$),
        tap((isLoading) => {
          Promise.resolve().then(() => {
            this.isLoading = isLoading;
            this.onLoadingChanged.emit(isLoading);
          });
        })
      )
      .subscribe();

    this._svcFeedService.feed$
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          Promise.resolve().then(() => {
            this.feeds = response?.feeds ?? [];
            if (this.selectedFeed) {
              const feed = this.feeds.find((f) => f.commentHeaderId === this.selectedFeed.commentHeaderId);
              if (feed && feed !== this.selectedFeed) {
                this.selectedFeed = feed;
              } else if (!feed) {
                this.selectedFeed = null;
              }
            }
          });
        })
      )
      .subscribe();
  }

  private _listenFeedRemoved(): void {
    this._svcFeedService.feedRemoved$
      .pipe(
        takeUntil(this.destroy$),
        tap(({ commentHeaderId }) => {
          if (this.selectedFeed?.commentHeaderId === commentHeaderId) {
            Promise.resolve().then(() => (this.selectedFeed = null));
            const message = this._translocoService.translate(
              'Este chat não está mais disponível, pois o registro foi removido.'
            );
            this._svcToastService.error(message);
          }
        })
      )
      .subscribe();
  }

  private _listenTotaUnread() {
    this._svcFeedService.totalUnreadIsLoading$
      .pipe(
        takeUntil(this.destroy$),
        tap((isLoading) => {
          Promise.resolve().then(() => {
            this.isTotalLoading = isLoading;
            if (isLoading) {
              this.onTotalChanged.emit(null);
            }
          });
        })
      )
      .subscribe();

    this._svcFeedService.totalUnread$
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          Promise.resolve().then(() => {
            this.total = response ?? 0;
            if (response != null) {
              this.onTotalChanged.emit(response);
            }
          });
        })
      )
      .subscribe();
  }

  private _listenSignalR() {
    this._svcFeedService.startListenSignalR();
    this._svcFeedService.newFeedReceived$
      .pipe(
        takeUntil(this.destroy$),
        tap((feed) => {
          if (feed.typeNotification === SvcFeedNoticationType.REGISTRY_DELETED) {
            if (this.selectedFeed && this.selectedFeed.commentHeaderId === feed.commentHeaderId) {
              this.selectedFeed = null;
            }
            return;
          }

          if (!this.selectedFeed || !feed.commentId) return;

          const defaultUid = '00000000-0000-0000-0000-000000000000';
          const selectedFeed = this.selectedFeed;
          if (
            (!!feed.registryId && !!selectedFeed.registryId && feed.registryId === selectedFeed.registryId) ||
            (feed.registryUid !== defaultUid && selectedFeed.registryUid !== defaultUid && feed.registryUid === selectedFeed.registryUid)
          ) {
            feed.totalNoRead = 0;
            this.svcCommentsViewer?.tryToRefreshChatByCommentId(feed.commentId);
          }
        })
      )
      .subscribe();
  }

  private _getFilters() {
    this.isFiltersLoading = true;
    this._svcFeedService
      .getFilters()
      .pipe(
        tap((response) => {
          this._mountFiters(response);
        }),
        catchError((error) => {
          this._mountFiters();
          return error;
        }),
        finalize(() => (this.isFiltersLoading = false))
      )
      .subscribe();
  }

  private _mountFiters(options?: { applications?: Application[] }) {
    this.filtersField = [
      {
        name: 'applicationIds',
        label: this._translocoService.translate('Módulo'),
        initialValue: [],
        type: 'chip',
        config: {
          options:
            options?.applications?.map(
              (item) =>
                <ISvcChipOption>{
                  value: item.id,
                  label: item.description,
                }
            ) ?? [],
        },
      },
      {
        name: 'startDate',
        label: this._translocoService.translate('Período'),
        type: 'date',
        dependsAnother: 'endDate',
        config: {
          maxDate: new Date(),
        },
      },
      {
        name: 'endDate',
        label: '',
        type: 'date',
        dependsAnother: 'startDate',
        config: {
          maxDate: new Date(),
        },
      },
    ];
  }

  private _listenSearchControl() {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(500),
        tap((value) => {
          this.lastFilters = {
            ...(this.lastFilters ?? {}),
            filter: value,
          };
          this._svcFeedService.getFeed(this.lastFilters);
        })
      )
      .subscribe();
  }

  private _checkIfHasFilters() {
    const lastFilters = { ...this.lastFilters };
    delete lastFilters.filter;
    this.hasFiltersDefined = Object.keys(lastFilters).some((key) => {
      const value = lastFilters[key];
      if (!!value || (Array.isArray(value) && value.length > 0)) {
        return true;
      }
      return false;
    });
  }

  public closeSidebarIfOpened() {
    this.onCloseSidebarWasNedded.emit();
  }

  public ngOnDestroy(): void {
    this._svcFeedService.stopListenSignalR();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
